import { ButtonProps } from "@/components/_rebrand/Button/Button.types";
import { TextCombination } from "@/components/_rebrand/TextCombination";
import Image from "next/image";
import { Button } from "../Button";
import Grid from "../Grid";
import ThemeWrapper from "../ThemeWrapper";

export type WhatsNewCtaProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  button: ButtonProps;
  mobileImageSrc: string;
  desktopImageSrc: string;
}

const DesktopBackgroundPattern = () => {
  return (
    <svg width="1464" height="377" viewBox="0 0 1464 377" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_f_1_375)">
        <ellipse cx="732" cy="-539.5" rx="926" ry="916.5" fill="#A857F1" />
      </g>
      <defs>
        <filter id="filter0_f_1_375" x="-494" y="-1756" width="2452" height="2433" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="150" result="effect1_foregroundBlur_1_375" />
        </filter>
      </defs>
    </svg>
  );
}

const MobileBackgroundPattern = () => {
  return (
    <svg className="w-full" width="406" height="604" viewBox="0 0 406 604" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_f_1_1501)">
        <ellipse cx="203" cy="-547.5" rx="926" ry="916.5" fill="#A857F1" />
      </g>
      <defs>
        <filter id="filter0_f_1_1501" x="-1023" y="-1764" width="2452" height="2433" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="150" result="effect1_foregroundBlur_1_1501" />
        </filter>
      </defs>
    </svg>
  );
}

/**
 * Slightly experimental CTA requested by the brand team to test some more abstract patterns and designs.
 * To make it work, it requires some hard-coding of the background patterns and the image positioning.
 * Changing background patterns or image positioning would require some corresponding code changes. 
 */
export const WhatsNewCta = ({ eyebrow, title, description, button, mobileImageSrc, desktopImageSrc }: WhatsNewCtaProps) => {
  return (
    <ThemeWrapper theme="theme-dark-violet">
      <div className="surface-outer p-margin-outer">
        <div className="surface-l1 rounded-12 h-full relative overflow-hidden">
          <Grid className="relative z-2">
            <div className="col-span-4 md:col-span-6 space-y-xl pt-4xl md:pb-4xl">
              <TextCombination type="d3p1" align="left" eyebrow={eyebrow} title={title} description={description} />
              {button && (
                <Button action={button.action} href={button.href} variant="primary" buttonType="filled" text={button.text} />
              )}
            </div>
          </Grid>
          {/* Mobile image is fixed to the bottom of the container */}
          <div className="block md:hidden relative z-1 w-full">
            <Image src={mobileImageSrc} className="w-full" alt="What's New CTA" width={500} height={500} />
          </div>
          {/* Mobile decorative ellipse` */}
          <div className="block md:hidden absolute inset-0 z-0 w-full">
            <MobileBackgroundPattern />
          </div>
          {/* Desktop image is fixed to the right of the container */}
          <div className="hidden md:block absolute right-0 top-0 bottom-0 z-1 md:w-3/4 2xl:w-1/2">
            <Image src={desktopImageSrc} className="w-full h-full" alt="What's New CTA" width={500} height={500} />
          </div>
          {/* Desktop decorative ellipse` */}
          <div className="hidden md:block absolute bottom-0 left-0 right-0 z-0">
            <DesktopBackgroundPattern />
          </div>
        </div>
      </div>
    </ThemeWrapper>
  );
}
